

<script setup>
  import { ref, computed } from 'vue';
  import UserAvatar from '@/components/userAvatar.vue';
  import { useUserStore } from '@/stores/user';

  const props = defineProps({
    salesPitchList: {
      type: Array,
      default: () => [],
    },
    isDesktop: {
      type: Boolean,
      default: false,
    },
    defaultOpen: {
      type: Boolean,
      default: true,
    },
  });

  const isOpen = ref(props.defaultOpen);
  const userStore = useUserStore();

  function getUserBySalesAgentId(salesAgentId) {
    if (!salesAgentId) return null;
    return userStore.colleagues.find(u => u.isSalesAgent && Number(u.isSalesAgent.id) === Number(salesAgentId)) || null;
  }

  const historyCount = computed(() => {
    try {
      const arr = Array.isArray(props.salesPitchList) ? props.salesPitchList : [];
      return Math.max(arr.filter(n => n && n.date != null).length - 1, 0);
    } catch (e) {
      return 0;
    }
  });

  const groupedNotes = computed(() => {
    try {
      const arr = Array.isArray(props.salesPitchList) ? props.salesPitchList : [];
      const groups = [];
      let current = null;
      for (const n of arr) {
        if (!n || n.id === -1) continue;
        const hasDate = n.date != null;
        if (hasDate) {
          if (current) groups.push(current);
          current = {
            date: n.date,
            contact: n.contact || null,
            items: [],
            key: `d:${String(n.date)}:${String(n.id ?? Math.random())}`,
          };
        } else if (!current) {
          current = {
            date: null,
            contact: n.contact || null,
            items: [],
            key: `nodate:${String(n.id ?? Math.random())}`,
          };
        }
        if (current) current.items.push(n);
      }
      if (current) groups.push(current);
      return groups;
    } catch (e) {
      return [];
    }
  });

  function formatContact(contact) {
    try {
      if (!contact || typeof contact !== 'object') return 'Unbekannt';
      const sal = contact.gender === 'f' ? 'Frau' : (contact.gender === 'm' ? 'Herr' : '');
      const first = contact.firstName ? String(contact.firstName).trim() : '';
      const last = contact.lastName ? String(contact.lastName).trim() : '';
      const name = `${first} ${last}`.trim();
      return [sal, name].filter(Boolean).join(' ').trim() || 'Unbekannt';
    } catch (e) {
      return 'Unbekannt';
    }
  }

  function hasKnownContact(contact) {
    try {
      if (!contact || typeof contact !== 'object') return false;
      const id = String(contact.id ?? '').trim();
      if (id === '__unknown__') return false;
      const hasName = !!String(contact.firstName || '').trim() || !!String(contact.lastName || '').trim();
      const hasEmail = !!String(contact.email || '').trim()
        || (Array.isArray(contact.emailAddresses) && String(contact.emailAddresses[0] || '').trim().length > 0)
        || (Array.isArray(contact.emailList) && String(contact.emailList[0]?.name || '').trim().length > 0);
      return hasName || hasEmail;
    } catch (e) {
      return false;
    }
  }

  function formatDateOnly(dateStr) {
    // Erwartetes Format: '07.05.26, 15:21, Franziska B.'
    if (!dateStr) return '';
    const parts = String(dateStr).split(',');
    if (parts.length >= 2) {
      return parts[0].trim() + ', ' + parts[1].trim();
    }
    return dateStr;
  }
</script>

<template>
  <div :class="['accordion-menu notes-details', { 'preventOnDesktop': isDesktop }]">
    <ul class="main-list">
      <li class="main-list-item">
        <input type="checkbox" v-model="isOpen">
        <i class="arrow"></i>
        <h3>
          <b>{{ historyCount }}</b>
          Verlauf Kontaktierungsstatus
        </h3>
        <div class="accordion-content-wrapper" v-show="isOpen">
          <div class="notes-details-content">
            <div class="notes-wrapper" v-for="grp in groupedNotes" :key="grp.key">
              <div class="note">
                <div v-if="grp.date !== null" class="meta-info">
                  <div v-if="grp.items[0]?.assignedBy" class="meta-block meta-assigned-by">
                    Zugewiesen von: 
                    <span class="icon-user" aria-hidden="true"></span>
                    <span class="name">{{ grp.items[0]?.assignedBy }}</span>
                    an
                  </div>
                  <div v-if="grp.items && grp.items[0]?.salesAgent" class="meta-block meta-salesmanager">
                    <UserAvatar v-if="getUserBySalesAgentId(grp.items[0].salesAgent.id)"
                      :idUser="getUserBySalesAgentId(grp.items[0].salesAgent.id)?.id"
                      :salesAgentId="grp.items[0].salesAgent.id"
                      :hideStatus="true"
                      :hideText="false"
                      :hideRank="true"
                      :size="22"
                      style="vertical-align:middle; margin-right:3px;"
                    />
                    <span v-else style="color:#888; font-style:italic;">Unbekannt</span>
                  </div>
                  <div class="meta-block meta-date">
                    <span class="icon-clock" aria-hidden="true"></span>
                    <span>{{ formatDateOnly(grp.date) }}</span>
                  </div>
                  <div v-if="hasKnownContact(grp.contact)" class="meta-block meta-contact">
                    Entscheider:
                    <span class="icon-user" aria-hidden="true"></span>
                    <span class="name">{{ formatContact(grp.contact) }}</span>
                  </div>
                </div>
                <template v-for="note in grp.items" :key="note.id">
                  <div class="status information" :id="'_' + (note?.status?.id ?? '')">
                    <div class="name" v-if="note?.dateFollowUp === null">
                      <span>{{ note?.status?.name }}</span>
                    </div>
                    <div class="dateFollowUp" v-else>
                      <span>{{ note?.status?.listAs }}: </span>
                      <span>{{ note?.dateFollowUp }}</span>
                    </div>
                  </div>
                  <div class="status information" v-if="note?.comment !== null">
                    <div class="comment">
                      <span>Notiz: </span>
                      <span>{{ note?.comment }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.notes-details-content { display:grid; gap:.6rem; }
.notes-wrapper .note { background:linear-gradient(145deg,#ffffff,#f6f9fb); border:1px solid #d5dee7; border-radius:12px; padding:.6rem .75rem; box-shadow:0 8px 16px -14px rgba(30,41,59,.22), 0 1px 3px rgba(30,41,59,.1); transition: box-shadow .2s ease, transform .2s ease; }
.notes-wrapper .note:hover { transform: translateY(-1px); box-shadow:0 12px 22px -16px rgba(30,41,59,.25), 0 4px 10px -6px rgba(30,41,59,.12); }

.sm-wrapper { padding: 0 }

.meta-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.8rem;
  align-items: center;
  justify-content: space-between;
  font-size: .74rem;
  font-weight: 600;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: .28rem .6rem;
  margin-bottom: 0.12rem;
}

/* Desktop: place date to the far right, other items use remaining space */
@media (min-width: 1024px) {
  .meta-info { gap: 0.4rem 1rem; }
  .meta-date { order: 2; margin-left: auto; white-space: nowrap; }
  .meta-assigned-by { order: 0; width: 100%; }
  .meta-salesmanager { order: 1; display:flex; align-items:center; gap:.4rem; min-width:0; }
  .meta-contact { order: 3; display:flex; align-items:center; gap:.4rem; min-width:0; }
}

/* Ensure long names don't force layout expansion */
.meta-block { display:flex; align-items:center; gap:.32rem; min-width:0; }
.meta-block .name { min-width:0; max-width:28rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.meta-salesmanager .salesmanager-name, .meta-contact .name { font-weight:600; }

.meta-salesmanager{
  grid-area: salesmanager;
}

.meta-date {
  grid-area: date;
}

.meta-contact {
  grid-area: contact;
}

.meta-assigned-by {
  grid-area: assigned-by;
}

.meta-block {
  display: flex;
  align-items: center;
  gap: .32rem;
  min-width: 0;
}
.meta-date .icon-clock {
  width: 13px;
  height: 13px;
  display: inline-block;
  background: url('@/assets/imgs/icons/clock-solid.svg') no-repeat center / contain;
  opacity: .7;
}
.meta-contact .icon-user {
  width: 13px;
  height: 13px;
  display: inline-block;
  background: url('@/assets/imgs/icons/user-solid.svg') no-repeat center / contain;
  opacity: .7;
}
.meta-contact .name {
  letter-spacing: .25px;
}
.meta-salesmanager .salesmanager-name {
  margin-left: 1px;
  font-weight: 600;
  letter-spacing: .18px;
}

.status.information { margin-top:.45rem; background:#f8fafc; border:1px solid #dfe5eb; border-radius:10px; padding:.4rem .55rem; }
.status.information .name span { font-weight:700; letter-spacing:.35px; }
.status.information .dateFollowUp span:first-child { font-weight:700; letter-spacing:.35px; }

.status.information + .status.information { margin-top:.35rem; }
.status.information .comment { display:flex; align-items:baseline; gap:.35rem; font-size:.66rem; }

/* Dark theme overrides using app theme class (and common dark markers) */
:global(.theme--dark) .notes-wrapper .note, :global(.dark) .notes-wrapper .note { background:linear-gradient(145deg,#1f2731,#1b2229); border-color:#334155; box-shadow:0 10px 24px -18px rgba(0,0,0,.6), 0 2px 6px -3px rgba(0,0,0,.35); }
:global(.theme--dark) .notes-wrapper .note:hover, :global(.dark) .notes-wrapper .note:hover { box-shadow:0 14px 28px -18px rgba(0,0,0,.65), 0 4px 10px -4px rgba(0,0,0,.35); }

:global(.theme--dark) .meta-info, :global(.dark) .meta-info { color:#e2e8f0; background:rgba(255,255,255,.06); border-color:rgba(255,255,255,.12); }
:global(.theme--dark) .meta-date .icon-clock, :global(.dark) .meta-date .icon-clock, :global(.theme--dark) .meta-contact .icon-user, :global(.dark) .meta-contact .icon-user { opacity:.85; filter:drop-shadow(0 0 1px rgba(0,0,0,.25)); }
:global(.theme--dark) .status.information, :global(.dark) .status.information { background:rgba(255,255,255,.05); border-color:rgba(255,255,255,.12); }
</style>
